import { Body, Modal } from './base';
import { Flex } from '@components/layout/Grid';
import Icon from '@components/Icon';
import styled from 'styled-components';
import Button from '@components/buttons';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { UserContext } from '@lib/contexts/UserProvider';
import {
  EligibleForTrialReclaimKind,
  UserDecisionKind,
  useStoreUserDecisionMutation,
} from '@gql/generated';
import LanguageContext from '@lib/contexts/languageContext';
import Link from 'next/link';
import AnalyticsManager from '@lib/analytics/manager';
import useFlags from '@lib/hooks/useFlags';

const ModalBody = styled(Body)`
  padding: 0;
`;

const ImageContainer = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(
      90deg,
      rgba(157, 201, 199, 0.7),
      rgba(157, 201, 199, 0.7)
    ),
    url('/static/cheerful-sportswomen-giving-high-five-to-each-other.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  i:before {
    font-weight: 400;
  }
`;

const Title = styled.h2`
  font-size: 20px;
`;

const Desc = styled.p`
  font-size: 14px;
  line-height: 23px;
  text-align: center;
  margin: 0;
`;

const ClaimTrial = styled(Button)`
  width: 100%;
  font-size: 14px;
  margin-top: 20px;
  margin-bottom: 13px;
  background-color: rgb(17, 158, 221);

  :hover {
    background-color: rgba(17, 158, 221, 0.9);
  }
`;

const NotInterestedButton = styled(Button)`
  font-size: 14px;
  color: rgb(18, 158, 220);
`;

const ENABLED_PATHS = [
  { path: '/[lang]/live', exact: false },
  { path: '/[lang]/playlists', exact: false },
  { path: '/[lang]/videos', exact: false },
  { path: '/[lang]/me', exact: false },
];

export const TrialClaimModal = () => {
  const router = useRouter();
  const [flags] = useFlags();
  const lang = useContext(LanguageContext);
  const [currentUser, { refetch }] = useContext(UserContext);
  const [storeUserDecision, { loading }] = useStoreUserDecisionMutation({
    onCompleted(resp) {
      if (resp?.storeUserDecision?.user?.id) {
        AnalyticsManager().sendTrialRejected();
        refetch({ onlyBaseProperties: true });
      }
    },
  });
  //

  const allowed_on_page = ENABLED_PATHS.some(item => {
    if (item.exact && item.path === router.pathname) {
      return true;
    } else {
      return router.pathname.startsWith(item.path);
    }
  });

  if (
    !(
      flags.enable_trialclaim_modal &&
      allowed_on_page &&
      currentUser?.eligibleForTrialReclaim ===
        EligibleForTrialReclaimKind['Available']
    )
  ) {
    return null;
  }

  return (
    <Modal open={true} customWidth="500px">
      <ModalBody>
        <ImageContainer>
          <Icon
            name="ygb-icon-yogobe-logo2019_1"
            fontSize="24px"
            color="#fff"
          />
        </ImageContainer>
        <Flex
          maxWidth="428px"
          flexDirection="column"
          alignItems="center"
          px={20}
          pt={10}
          pb={30}
          mx="auto"
        >
          <Title>
            <Trans id="reclaim_trial_modal.title" />
          </Title>
          <Desc
            dangerouslySetInnerHTML={{
              __html: t`reclaim_trial_modal.desc`,
            }}
          ></Desc>
          <Link
            href={{
              pathname: `/${lang}/account/reclaimtrial`,
              query: {
                code: 'provayogobe14',
                source: 'modal',
              },
            }}
            passHref
            legacyBehavior>
            <ClaimTrial appearance="default-blue">
              <Trans id="reclaim_trial_modal.claim_btn" />
            </ClaimTrial>
          </Link>
          <NotInterestedButton
            appearance="link"
            loading={loading}
            onClick={() => {
              storeUserDecision({
                variables: {
                  decision: UserDecisionKind['ProvaYogobe14'],
                  value: false,
                },
              });
            }}
          >
            <Trans id="reclaim_trial_modal.not_intrested_btn" />
          </NotInterestedButton>
        </Flex>
      </ModalBody>
    </Modal>
  );
};
